import { Box, Card, Flex, HStack, Icon, Skeleton, Text, VStack } from "@chakra-ui/react"
import Popover from "@hydrologiq/shared/chakra/Popover"
import { FaPlugCircleExclamation } from "react-icons/fa6"

export interface PopOverProps {
  title: string
  content?: React.ReactNode
}

export interface FigureProps {
  heading?: string
  /** Glyph rendered immediately before the heading text (e.g. a KPI category icon) */
  headingIcon?: React.ReactNode
  /** Content rendered immediately after heading in the top header row (e.g. data connection indicator) */
  headingExtra?: React.ReactNode
  popover?: PopOverProps
  headerRight?: React.ReactNode
  children?: React.ReactNode
  isPlaceholder?: boolean
  error?: string
  loading?: boolean
  width?: string | Record<string, string>
  height?: string | Record<string, string>
  minH?: string | Record<string, string>
  /** Shorter card for dense strips (e.g. system overview headlines) */
  compact?: boolean
}

const defaultWidth = { base: "100%", sm: "250px", "2xl": "320px" } as const

/**
 * Tones down the shared icon-button treatment for the info popover: it defaults to a 32px square
 * that fills solid cyan on hover/open, which dominates a 92px KPI card. Here it's a 14px glyph that
 * only tints. Scoped to this card so the other Popover call sites keep the standard treatment.
 *
 * Every rule is anchored to the trigger because the popover renders unportalled — its panel content
 * sits inside the same wrapper and must not be restyled.
 *
 * `!important` on the colours is unavoidable: Popover's own trigger css sets them via a
 * `> div > svg` rule that outranks anything selectable from outside the trigger.
 */
const TRIGGER = "& [data-scope='popover'][data-part='trigger']"
const infoTriggerCss = {
  [`${TRIGGER} button`]: {
    padding: "2px",
    minWidth: "auto",
    width: "auto",
    height: "auto",
    color: "#a1a1aa !important",
    _hover: {
      color: "#52525b !important",
      background: "transparent !important",
    },
    _expanded: {
      color: "#0891b2 !important",
      background: "transparent !important",
    },
  },
  /* Hide the tooltip while the popover is open. Popover's trigger CSS can reach this because
     Tooltip renders unportalled by default too. Stops the "Click for info" balloon from sitting on
     top of the popover content. */
  [`&:has([data-scope='popover'][data-part='trigger'][data-state='open']) [data-scope='tooltip']`]:
    {
      display: "none",
    },
  /* Let clicks pass through tooltip positioner/content elements to avoid blocking the trigger
     during mouse transitions. Fixes tooltip remaining visible and blocking clicks due to the
     `interactive` prop being dropped in chakra/Tooltip.tsx. */
  "& [data-scope='tooltip'][data-part='positioner'], & [data-scope='tooltip'][data-part='content']": {
    pointerEvents: "none",
  },
}

function Figure({
  heading,
  headingIcon,
  headingExtra,
  headerRight,
  children,
  popover,
  isPlaceholder,
  error,
  loading,
  width: _width = defaultWidth,
  height = "100%",
  minH,
  compact = true,
}: FigureProps) {
  const showContent = !(isPlaceholder || error || loading)
  return (
    <Card.Root
      height={height}
      minH={
        minH !== undefined
          ? minH
          : compact
            ? { base: "72px", lg: "82px", "2xl": "92px" }
            : { base: "130px", lg: "140px", "2xl": "160px" }
      }
      width="100%"
      variant={"kpi" as any}
      overflow="hidden"
    >
      {/* Line 1: heading + optional pager / popover. Centred so the title sits on the same line as
          the "1 of 3" text and the info icon; the wrapper below keeps the controls from setting the
          row's height. */}
      <Flex justifyContent="space-between" alignItems="center" mb="6px" gap={2}>
        {(heading || headingIcon || headingExtra) && (
          <HStack gap="9px" alignItems="center" flex="1 1 auto" minWidth={0}>
            {headingIcon && (
              <Box display="flex" alignItems="center" flexShrink={0} fontSize="13px" lineHeight="1">
                {headingIcon}
              </Box>
            )}
            {heading && (
              <Text
                as="h3"
                fontSize="13px"
                fontWeight="600"
                color="primary"
                lineHeight="1.2"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {heading}
              </Text>
            )}
            {headingExtra}
          </HStack>
        )}
        {(showContent && popover || headerRight) && (
          // Zero-height wrapper: pager buttons (~24px) are taller than the heading's line box
          // (15.6px), so in flow they grew this row and pushed the paginated cards several px below
          // the rest of the strip. At 0px they contribute width only, still centred on the
          // heading's line, so the header's height comes from the heading alone (and a long one can
          // still wrap).
          <Box height="0px" display="flex" alignItems="center" flexShrink={0}>
            <HStack gap={2} alignItems="center">
              {headerRight}
              {showContent && popover && (
                <Box css={infoTriggerCss}>
                  <Popover placement="right-end" iconType="info" closeOnOutside={true} >
                    <VStack width={"100%"} minWidth={"300px"} padding={4} margin={0} gap={2}>
                      {popover.content}
                    </VStack>
                  </Popover>
                </Box>
              )}
            </HStack>
          </Box>
        )}
      </Flex>

      {/* Lines 2+: value and optional trend */}
      <Card.Body padding="0" flex="1" display="flex" flexDirection="column" justifyContent="flex-start">
        {showContent ? (
          children
        ) : loading ? (
          <VStack gap={1.5} alignItems="flex-start" width="100%">
            <Skeleton data-testid="skeleton" height="13px" width="60%" />
            <Skeleton height={compact ? "24px" : "28px"} width="80%" />
          </VStack>
        ) : error ? (
          <VStack alignItems="flex-start" gap={1}>
            <Icon as={FaPlugCircleExclamation} />
            <Text color="primary_light">{error}</Text>
          </VStack>
        ) : (
          <VStack gap={1.5} alignItems="flex-start" width="100%">
            <Text color="primary_light">&nbsp;</Text>
            <Text color="primary_light">Waiting for more data</Text>
          </VStack>
        )}
      </Card.Body>
    </Card.Root>
  )
}

export default Figure
